import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CircularProgress, Stack, Typography } from '@mui/material';
import { Button, Link, LoadingWrapper } from 'components/custom';
import { Flow, handleErrorFallback, handleFlowError, handleGetFlowError, ory, useLogoutHandler, } from 'components/kratos';
import { showError } from 'components/kratos/skyla/helpers';
import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthUrl } from 'utils/config';
import { setTitle } from 'utils/head';
import { HttpStatusCodes } from 'utils/http-status-codes';
import { isInIframe, isIframeTokenValid, setIframeToken } from 'utils/iframe';
import { HOME, LOGIN, PASSWORD_RECOVERY } from 'utils/links';
import { RouterStateKeys } from 'utils/routing';
import { useSidebarLayoutStyles } from 'layouts/Sidebar/styles';
/**
 * Displays login form.
 */
const Login = () => {
    const { t } = useTranslation('auth');
    const [flow, setFlow] = useState();
    const [loading, setLoading] = React.useState(true);
    const navigate = useNavigate();
    // Get ?flow=... from the URL
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('return_to');
    const flowId = searchParams.get('flow');
    /*
     * Refresh means we want to refresh the session. This is needed, for example, when we want to update the password
     * of a user.
     */
    const refresh = searchParams.get('refresh');
    /*
     * AAL = Authorization Assurance Level. This implies that we want to upgrade the AAL, meaning that we want
     * to perform two-factor authentication/verification.
     */
    const aal = searchParams.get('aal');
    /*
     * This might be confusing, but we want to show the user an option
     * to sign out if they are performing two-factor authentication!
     */
    const onLogout = useLogoutHandler();
    // IFrame state
    const [showIframeLogin, setShowIframeLogin] = useState(false);
    const [iframeReturnTo, setIframeReturnTo] = useState(null);
    /*
     * Popup Auto-SSO: When popup=true is in the URL, this page was opened as a popup
     * from the iFrame SSO flow. The user should NOT see the login form — instead we show
     * a loading spinner + message while the automatic SSO redirect happens in the background.
     * This prevents the user from accidentally clicking on form elements and interrupting
     * the auto-redirect process.
     */
    const isPopupAutoSSO = searchParams.get('popup') === 'true' &&
        searchParams.get('login_challenge') !== null;
    /**
     * Navigate helper that handles both absolute and relative URLs.
     * React Router v6 navigate() does NOT support absolute URLs with protocol/domain.
     * Since handleUntisSSO() builds absolute return_to URLs, we must normalize them.
     */
    const navigateToUrl = (url) => {
        if (!url) {
            navigate(HOME, { replace: true });
            return;
        }
        if (url.startsWith(window.location.origin)) {
            /*
             * When return_to equals the bare origin (no path), replace produces "".
             * Fall back to '/' so React Router navigates to home instead of doing nothing.
             */
            navigate(url.replace(window.location.origin, '') || '/', {
                replace: true,
            });
        }
        else if (url.startsWith('/')) {
            navigate(url, { replace: true });
        }
        else {
            window.location.href = url;
        }
    };
    useEffect(() => {
        setTitle(t('head.login.title'));
    }, [t]);
    useEffect(() => {
        // If we already have a flow, do nothing.
        if (flow) {
            return;
        }
        /*
         * Skip regular flow initialization when in popup Auto-SSO mode.
         * The Mode 3 useEffect below handles flow creation for the SSO redirect.
         * Without this guard, both useEffects race to create a flow — the regular one
         * may complete first, set flow + loading=false, and briefly render the login form.
         */
        if (isPopupAutoSSO) {
            return;
        }
        setLoading(true);
        // If ?flow=.. was in the URL, we fetch it
        if (flowId) {
            ory
                .getLoginFlow({ id: String(flowId) })
                .then(({ data }) => {
                setFlow(data);
            })
                .catch(handleGetFlowError(navigate, 'login', setFlow, t))
                .catch(handleErrorFallback(t))
                .finally(() => {
                setLoading(false);
            });
            return;
        }
        // Otherwise we initialize it
        ory
            .createBrowserLoginFlow({
            aal: aal ? String(aal) : undefined,
            refresh: Boolean(refresh),
            returnTo: returnTo ? String(returnTo) : undefined,
        })
            .then(({ data }) => {
            setFlow(data);
        })
            .catch(handleFlowError(navigate, 'login', setFlow, t))
            .catch(handleErrorFallback(t))
            .finally(() => {
            setLoading(false);
        });
    }, [flowId, navigate, aal, refresh, returnTo, flow, t]);
    /* IFrame Mode: Modes 1, 2, and 4 */
    // IFrame detection useEffect (runs before the Auto-SSO useEffect)
    useEffect(() => {
        const loginChallenge = searchParams.get('login_challenge');
        // MODE 1: iFrame with valid token → redirect immediately (no user interaction)
        if (isInIframe() && isIframeTokenValid() && loginChallenge) {
            navigateToUrl(returnTo);
            return;
        }
        // MODE 2: iFrame without valid token + login_challenge → show login button
        if (isInIframe() && !isIframeTokenValid() && loginChallenge) {
            setIframeReturnTo(returnTo);
            setShowIframeLogin(true);
            setLoading(false);
            return;
        }
        /*
         * MODE 4: iFrame + no token + no login_challenge → session expired.
         * This happens when Bearer token expires mid-session and 401 navigates to /login.
         */
        if (isInIframe() && !isIframeTokenValid() && !loginChallenge) {
            setLoading(false);
            // Render is handled below in the component return
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, returnTo]);
    // PostMessage listener: receives token from popup after SSO completion
    useEffect(() => {
        if (!showIframeLogin)
            return undefined;
        const handler = (event) => {
            if (event.origin !== window.location.origin)
                return;
            const data = event.data;
            if (data.type === 'SSO_COMPLETE' && data.token) {
                setIframeToken(data.token);
                navigateToUrl(iframeReturnTo);
            }
        };
        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showIframeLogin, iframeReturnTo]);
    /* Mode 3: Normal / Popup Auto-SSO (existing Untis login_challenge handling) */
    // Handle Untis SSO login_challenge JWT (MODE 3: non-iFrame context)
    useEffect(() => {
        const loginChallenge = searchParams.get('login_challenge');
        // Only process if login_challenge is present and we don't have a flow yet
        if (!loginChallenge || flow) {
            return;
        }
        // IFrame mode is handled by the dedicated iFrame useEffect above
        if (isInIframe())
            return;
        setLoading(true);
        try {
            // Decode JWT client-side to extract tenant_id
            const decoded = jwtDecode(loginChallenge);
            const tenantId = decoded.tenant_id;
            /*
             * Set cookie with tenant_id BEFORE starting Kratos flow.
             * This cookie will be automatically sent to all subdomains during OAuth flow.
             * Cookie domain is derived dynamically from hostname (works on staging AND production).
             */
            const cookieDomain = `.${window.location.hostname
                .split('.')
                .slice(1)
                .join('.')}`;
            document.cookie = `untis_tenant_id=${tenantId}; domain=${cookieDomain}; path=/; secure; samesite=lax; max-age=600`;
            // Create Kratos browser login flow
            ory
                .createBrowserLoginFlow({
                returnTo: returnTo ? String(returnTo) : undefined,
            })
                .then(({ data }) => {
                /**
                 * Flow created, now submit POST form to initiate OIDC flow
                 * (Kratos requires POST to /self-service/login with provider field)
                 */
                // Extract CSRF token from flow
                const csrfNode = data.ui.nodes.find((node) => 'name' in node.attributes &&
                    node.attributes.name === 'csrf_token');
                if (!csrfNode || !('value' in csrfNode.attributes)) {
                    throw new Error('CSRF token not found in login flow');
                }
                const csrfToken = String(csrfNode.attributes.value);
                // Create and submit form with POST method
                const kratosUrl = getAuthUrl();
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = `${kratosUrl}/self-service/login?flow=${data.id}`;
                form.style.display = 'none';
                // Add provider field (tells Kratos which OIDC provider to use)
                const providerInput = document.createElement('input');
                providerInput.type = 'hidden';
                providerInput.name = 'provider';
                providerInput.value = 'UntisSSO';
                form.appendChild(providerInput);
                // Add CSRF token
                const csrfInput = document.createElement('input');
                csrfInput.type = 'hidden';
                csrfInput.name = 'csrf_token';
                csrfInput.value = csrfToken;
                form.appendChild(csrfInput);
                // Add tenant_id (will be passed to Authentik via OIDC flow)
                const tenantInput = document.createElement('input');
                tenantInput.type = 'hidden';
                tenantInput.name = 'tenant_id';
                tenantInput.value = tenantId;
                form.appendChild(tenantInput);
                // Submit form
                document.body.appendChild(form);
                form.submit();
            })
                .catch(handleFlowError(navigate, 'login', setFlow, t))
                .catch(handleErrorFallback(t))
                .finally(() => {
                setLoading(false);
            });
        }
        catch (error) {
            // JWT decode error or other error - log and handle gracefully
            const errorMessage = t('body.flow.error.untisLoginChallengeFailed');
            showError(errorMessage, error);
            // Navigate to regular login page on error
            navigate(LOGIN, { replace: true });
            setLoading(false);
        }
    }, [searchParams, flow, returnTo, navigate, t]);
    const onSubmit = async (values) => {
        setLoading(true);
        /*
         * On submission, add the flow ID to the URL but do not navigate. This prevents the user loosing
         * his data when she/he reloads the page.
         */
        navigate(`${LOGIN}?flow=${String(flow?.id)}`, { replace: true });
        return (ory
            .updateLoginFlow({
            flow: String(flow?.id),
            updateLoginFlowBody: values,
        })
            // We logged in successfully! Let's bring the user home.
            .then(async () => {
            if (flow?.return_to) {
                // Handle local redirect.
                if (flow.return_to.startsWith('/')) {
                    // Replace the location, so that user cannot go back to the login flow.
                    navigate(flow.return_to, {
                        replace: true,
                        state: { [RouterStateKeys.AUTH]: true },
                    });
                }
                else {
                    window.location.href = flow.return_to;
                }
                return Promise.resolve();
            }
            navigate(HOME);
            return Promise.resolve();
        })
            .catch(handleFlowError(navigate, 'login', setFlow, t))
            .catch(async (err) => {
            // If the previous handler did not catch the error it's most likely a form validation error
            if (err.response?.status === HttpStatusCodes.BAD_REQUEST) {
                // Yup, it is!
                setFlow(err.response.data);
                return Promise.resolve();
            }
            return Promise.reject(err);
        })
            .catch(handleErrorFallback(t))
            .finally(() => {
            setLoading(false);
        }));
    };
    const { formStyles } = useSidebarLayoutStyles();
    // MODE 4: iFrame + no token + no login_challenge → session expired
    if (isInIframe() &&
        !isIframeTokenValid() &&
        !searchParams.get('login_challenge')) {
        return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 3, sx: { mt: 8 }, children: [_jsx(Typography, { variant: "h6", children: "Ihre Sitzung ist abgelaufen" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Klicken Sie auf eine Stunde in WebUntis, um sich erneut anzumelden." })] }));
    }
    /*
     * MODE 2: iFrame login (popup SSO).
     * The "Bei Skyla anmelden" button is now rendered in the SidePanel (SidebarLayout).
     * This early return prevents the normal login form from rendering in iFrame mode.
     * The postMessage listener useEffect above still runs to receive the token from the popup.
     * The Outlet container is hidden via display:none in SidebarLayout, so this content is not visible.
     */
    if (showIframeLogin) {
        return _jsx(_Fragment, {});
    }
    /*
     * Popup Auto-SSO: This page was opened as a popup from the iFrame SSO flow.
     * Show a loading spinner + message instead of the login form to prevent the user
     * from accidentally clicking on form elements and interrupting the automatic
     * SSO redirect process. The Mode 3 useEffect runs in the background and will
     * redirect the browser to Kratos → Authentik → WebUntis OAuth automatically.
     * This ONLY affects iFrame users (popup=true is only set by handleIframeLogin in SidebarLayout).
     */
    if (isPopupAutoSSO) {
        return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 3, sx: { mt: 8, px: 4, textAlign: 'center' }, children: [_jsx(CircularProgress, { size: 48 }), _jsx(Typography, { variant: "h6", children: "Automatische Anmeldung l\u00E4uft..." }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Sie werden in wenigen Sekunden automatisch angemeldet und weitergeleitet. Bitte schlie\u00DFen Sie dieses Fenster nicht." })] }));
    }
    // MODE 3: Normal login form (non-iFrame)
    return (_jsx(LoadingWrapper, { status: { loading }, children: _jsxs(Stack, { justifyContent: "center", spacing: formStyles.spacing, width: "100%", children: [_jsx(Typography, { align: "center", variant: "h5", sx: { fontWeight: 700 }, children: t('body.step.login.heading1') }), _jsx(Flow, { onSubmit: onSubmit, flow: flow, templateName: "login" }), (aal ?? refresh) ? (_jsx(Button, { onClick: () => {
                        void onLogout();
                    }, children: t('body.step.login.logoutButton') })) : (_jsx(Link, { align: "center", href: PASSWORD_RECOVERY, children: t('body.step.login.forgotPasswordButton') }))] }) }));
};
export default Login;
